/**
 * School Admin (tenet-level) IA — three navigation layers.
 *
 * 1. Primary sidebar: module → tab (Setup, Student, Document, …)
 * 2. Main page tabs: setup / contracts sub-pages only (Requirements, Auto-Notifications, …)
 * 3. Main panel: list and setup content for the active route
 */

import type { NavLinkSpec } from "@exxatdesignux/product-framework"
import { collectNavUrls } from "@exxatdesignux/ui/lib/nav-active"

import { filterSchoolAdminModules, canAccessSchoolAdminPath } from "@/lib/school-admin-access"
import { isTenantAdminTier } from "@/lib/workspace-rbac"

export type SchoolAdminModuleId =
  | "program-360"
  | "compliance"
  | "contracts-sites"
  | "placement-report"

export type SchoolAdminTab = {
  id: string
  label: string
}

export type SchoolAdminSetupLeaf = {
  id: string
  label: string
  href: string
  note: string
  icon: string
}

export type SchoolAdminModule = {
  id: SchoolAdminModuleId
  label: string
  icon: string
  basePath: string
  defaultTab: string
  tabs: SchoolAdminTab[]
  /** Sub-pages shown as main-page tabs when that primary child is selected. */
  secondaryByTab: Partial<Record<string, SchoolAdminSetupLeaf[]>>
  /** Placeholder copy keyed by full href. */
  notesByHref: Record<string, string>
}

export const SCHOOL_ADMIN_DEFAULT_HREF = "/admin/program-360/dashboard"

/** Tenet institute management STEPS demo (iframe). */
export const INSTITUTE_MANAGEMENT_DEMO_HREF = "/admin/tenant-apps"

/** Compliance Management student workspace — STEPS demo iframe in School Admin. */
export const SCHOOL_ADMIN_COMPLIANCE_IFRAME_HREF = "/admin/compliance/student"

/** Tab glyphs for content-area navigation (`fontawesome-subset.manifest.json`). */
export const SCHOOL_ADMIN_TAB_ICONS: Record<string, string> = {
  dashboard: "fa-chart-pie",
  summary: "fa-table",
  setup: "fa-sliders",
  student: "fa-user-graduate",
  document: "fa-file-lines",
  review: "fa-comments",
  approve: "fa-check",
  contracts: "fa-file-signature",
  sites: "fa-building",
  locations: "fa-location-dot",
}

export function schoolAdminTabIcon(tabId: string): string {
  return SCHOOL_ADMIN_TAB_ICONS[tabId] ?? "fa-circle"
}

/** Font Awesome glyphs from `fontawesome-subset.manifest.json`. */
const MODULE_ICONS: Record<SchoolAdminModuleId, string> = {
  "program-360": "fa-chart-mixed",
  compliance: "fa-shield-check",
  "contracts-sites": "fa-file-signature",
  "placement-report": "fa-chart-bar",
}

export const SCHOOL_ADMIN_MODULES: readonly SchoolAdminModule[] = [
  {
    id: "program-360",
    label: "Program 360",
    icon: MODULE_ICONS["program-360"],
    basePath: "/admin/program-360",
    defaultTab: "dashboard",
    tabs: [
      { id: "dashboard", label: "Dashboard by Program" },
      { id: "summary", label: "Summary" },
    ],
    secondaryByTab: {},
    notesByHref: {
      "/admin/program-360/dashboard":
        "Single-program KPI dashboard with six metric bands: Core Entities, Academics, Site and Contracts, Slots, Placements, Learning Activities.",
      "/admin/program-360/summary":
        "Cross-program matrix with program columns and Total. Shared entity counts may double-count when sites use a sharing model.",
    },
  },
  {
    id: "compliance",
    label: "Compliance Management",
    icon: MODULE_ICONS.compliance,
    basePath: "/admin/compliance",
    defaultTab: "setup",
    tabs: [
      { id: "setup", label: "Setup" },
      { id: "student", label: "Student" },
      { id: "document", label: "Document" },
      { id: "review", label: "Review Clarifications" },
      { id: "approve", label: "Approve" },
    ],
    secondaryByTab: {
      setup: [
        {
          id: "requirements",
          label: "Requirements",
          href: "/admin/compliance/setup/requirements",
          note: "Requirement configuration scoped by Program and Cohort.",
          icon: "fa-list-check",
        },
        {
          id: "auto-notifications",
          label: "Auto-Notifications",
          href: "/admin/compliance/setup/auto-notifications",
          note: "Email auto-notifications scoped by Program and Cohort.",
          icon: "fa-bell",
        },
        {
          id: "student-due-date",
          label: "Student Specific Due Date",
          href: "/admin/compliance/setup/student-due-date",
          note: "Student due dates by Program, Cohort, and Requirement.",
          icon: "fa-calendar-day",
        },
      ],
    },
    notesByHref: {
      "/admin/compliance/student":
        "Workspace-wide student compliance list with status, review, and export.",
      "/admin/compliance/document":
        "Document-centric list grouped by student for review workflows.",
      "/admin/compliance/review":
        "Approve-service workflow. Hidden when the school lacks Exxat Approve.",
      "/admin/compliance/approve":
        "Final approval queue when Approve is entitled.",
    },
  },
  {
    id: "contracts-sites",
    label: "Contract and Site Management",
    icon: MODULE_ICONS["contracts-sites"],
    basePath: "/admin/contracts-sites",
    defaultTab: "setup",
    tabs: [
      { id: "setup", label: "Setup" },
      { id: "contracts", label: "Contracts" },
      { id: "sites", label: "Sites" },
      { id: "locations", label: "Locations" },
    ],
    secondaryByTab: {
      setup: [
        {
          id: "site-sharing-model",
          label: "Site Sharing Model",
          href: "/admin/contracts-sites/setup/site-sharing-model",
          note: "Institution sharing policy for Site, Contract, and Location entities.",
          icon: "fa-share-nodes",
        },
        {
          id: "contract-organization",
          label: "Contract Organization",
          href: "/admin/contracts-sites/setup/contract-organization",
          note: "Routing Checklist tags and Contract Status lookup values.",
          icon: "fa-tags",
        },
        {
          id: "auto-notifications",
          label: "Auto Notifications",
          href: "/admin/contracts-sites/setup/auto-notifications",
          note: "Notification rules: trigger, action, recurrence, and active toggle.",
          icon: "fa-bell",
        },
      ],
      contracts: [
        {
          id: "contracts-dashboard",
          label: "Dashboard",
          href: "/admin/contracts-sites/contracts/dashboard",
          note: "Contract KPI tiles, status charts, and expiring contracts.",
          icon: "fa-chart-pie",
        },
        {
          id: "contracts-list",
          label: "List",
          href: "/admin/contracts-sites/contracts/list",
          note: "Searchable contract registry across programs.",
          icon: "fa-list",
        },
      ],
    },
    notesByHref: {
      "/admin/contracts-sites/sites":
        "Site list with Programs column, Active filter, and Add Site.",
      "/admin/contracts-sites/locations":
        "Location list with label name, address fields, and Active filter.",
    },
  },
  {
    id: "placement-report",
    label: "Placement Report",
    icon: MODULE_ICONS["placement-report"],
    basePath: "/admin/placement-report",
    defaultTab: "report",
    tabs: [],
    secondaryByTab: {},
    notesByHref: {
      "/admin/placement-report":
        "Placement report with Program filter, date range, export, column settings, and advanced filters.",
    },
  },
] as const

export function schoolAdminModulesForSession(programId: string | null = null): SchoolAdminModule[] {
  return filterSchoolAdminModules(SCHOOL_ADMIN_MODULES, programId)
}

/** First licensed tent route for this session (compliance-only admins skip Program 360). */
export function schoolAdminDefaultHrefForSession(programId: string | null = null): string {
  if (isTenantAdminTier()) return INSTITUTE_MANAGEMENT_DEMO_HREF
  const modules = schoolAdminModulesForSession(programId)
  const first = modules[0]
  if (!first) return "/admin"
  return schoolAdminModuleHref(first)
}

export function getSchoolAdminModule(id: string | undefined): SchoolAdminModule | undefined {
  return SCHOOL_ADMIN_MODULES.find(module => module.id === id)
}

export function schoolAdminModuleForPath(pathname: string): SchoolAdminModule | undefined {
  if (pathname === "/admin/placement-report") {
    return SCHOOL_ADMIN_MODULES.find(module => module.id === "placement-report")
  }
  return SCHOOL_ADMIN_MODULES.find(
    module => module.id !== "placement-report" && pathname.startsWith(`${module.basePath}/`),
  )
}

export type SchoolAdminRouteContext = {
  module: SchoolAdminModule
  tabId: string
  secondaryLeaf?: SchoolAdminSetupLeaf
}

export function parseSchoolAdminRoute(pathname: string): SchoolAdminRouteContext | null {
  if (pathname === "/admin/placement-report") {
    const module = getSchoolAdminModule("placement-report")
    if (!module) return null
    return { module, tabId: "report" }
  }

  for (const module of SCHOOL_ADMIN_MODULES) {
    if (module.id === "placement-report") continue
    if (!pathname.startsWith(`${module.basePath}/`)) continue

    const rest = pathname.slice(module.basePath.length + 1)
    const segments = rest.split("/").filter(Boolean)
    if (segments.length === 0) return null

    const tabId = segments[0]
    const tab = module.tabs.find(row => row.id === tabId)
    if (!tab) return null

    const secondaryItems = module.secondaryByTab[tabId]
    if (secondaryItems?.length) {
      const leaf = secondaryItems.find(item => item.href === pathname)
      if (leaf) return { module, tabId, secondaryLeaf: leaf }
      return null
    }

    if (segments.length === 1) {
      return { module, tabId }
    }
  }

  return null
}

/** Bare tab URLs (e.g. /admin/compliance/setup) redirect to the first setup leaf. */
export function schoolAdminCanonicalHref(pathname: string): string | null {
  const ctx = parseSchoolAdminRoute(pathname)
  if (ctx) return null
  for (const module of SCHOOL_ADMIN_MODULES) {
    if (module.id === "placement-report") continue
    if (!pathname.startsWith(`${module.basePath}/`)) continue
    const rest = pathname.slice(module.basePath.length + 1)
    const segments = rest.split("/").filter(Boolean)
    if (segments.length !== 1) continue
    const tabId = segments[0]
    const secondary = module.secondaryByTab[tabId]
    if (secondary?.[0]) return secondary[0].href
  }
  return null
}

export function schoolAdminTabHref(module: SchoolAdminModule, tabId: string): string {
  const secondary = module.secondaryByTab[tabId]
  if (secondary?.[0]) return secondary[0].href
  if (module.id === "placement-report") return module.basePath
  return `${module.basePath}/${tabId}`
}

/** Sidebar row href — tab prefix when sub-pages exist so every leaf keeps the row active. */
export function schoolAdminPrimaryTabHref(module: SchoolAdminModule, tabId: string): string {
  if (module.secondaryByTab[tabId]?.length) {
    return `${module.basePath}/${tabId}`
  }
  return schoolAdminTabHref(module, tabId)
}

/** Default landing route when a module row is selected in the primary sidebar. */
export function schoolAdminModuleHref(module: SchoolAdminModule): string {
  if (module.id === "placement-report") return module.basePath
  return schoolAdminTabHref(module, module.defaultTab)
}

export const SCHOOL_ADMIN_MODULE_LANDING_HREF = Object.fromEntries(
  SCHOOL_ADMIN_MODULES.map(module => [module.id, schoolAdminModuleHref(module)]),
) as Record<SchoolAdminModuleId, string>

export function schoolAdminSetupLeaves(module: SchoolAdminModule, tabId: string): SchoolAdminSetupLeaf[] {
  return module.secondaryByTab[tabId] ?? []
}

/** Program 360 and Placement Report are flat primary rows; tabs live on the page. */
export function schoolAdminHasPrimaryChildren(module: SchoolAdminModule): boolean {
  return module.id === "compliance" || module.id === "contracts-sites"
}

export function isSchoolAdminRoute(pathname: string): boolean {
  return parseSchoolAdminRoute(pathname) !== null
}

export function isInstituteManagementDemoPath(pathname: string): boolean {
  return (
    pathname === INSTITUTE_MANAGEMENT_DEMO_HREF ||
    pathname.startsWith(`${INSTITUTE_MANAGEMENT_DEMO_HREF}/`)
  )
}

/** True on every School Admin shell route, including bare tab URLs that redirect. */
export function isSchoolAdminShellPath(pathname: string): boolean {
  if (isInstituteManagementDemoPath(pathname)) return false
  if (isSchoolAdminRoute(pathname)) return true
  if (schoolAdminCanonicalHref(pathname) !== null) return true
  return SCHOOL_ADMIN_MODULES.some(
    module => module.id !== "placement-report" && pathname === module.basePath,
  )
}

/** Flat primary row stays lit for every route under the module base path. */
export function isSchoolAdminModuleNavActive(moduleId: SchoolAdminModuleId, pathname: string): boolean {
  const module = getSchoolAdminModule(moduleId)
  if (!module) return false
  if (module.id === "placement-report") return pathname === module.basePath
  return pathname === module.basePath || pathname.startsWith(`${module.basePath}/`)
}

/**
 * Primary sidebar child active when any route under that tab is open (including
 * setup sub-pages selected via main-page tabs).
 */
export function isSchoolAdminPrimaryChildActive(
  parentKey: string,
  childKey: string,
  pathname: string,
): boolean | undefined {
  if (!childKey.startsWith(`${parentKey}-`)) return undefined
  const module = getSchoolAdminModule(parentKey)
  if (!module) return undefined
  const tabId = childKey.slice(parentKey.length + 1)
  const tab = module.tabs.find(row => row.id === tabId)
  if (!tab) return undefined

  const tabPrefix = `${module.basePath}/${tabId}`
  if (module.secondaryByTab[tabId]?.length) {
    return pathname === tabPrefix || pathname.startsWith(`${tabPrefix}/`)
  }
  return pathname === tabPrefix
}

function schoolAdminPrimaryTabChild(module: SchoolAdminModule, tab: SchoolAdminTab): NavLinkSpec {
  const icon = schoolAdminTabIcon(tab.id)
  return {
    key: `${module.id}-${tab.id}`,
    title: tab.label,
    url: schoolAdminPrimaryTabHref(module, tab.id),
    iconClass: `fa-light ${icon}`,
    iconActiveClass: `fa-solid ${icon}`,
  }
}

/** Primary sidebar: Compliance / Contract trees; Program 360 and Placement Report are leaves. */
export function schoolAdminPrimaryNavSpec(programId: string | null = null): NavLinkSpec[] {
  return schoolAdminModulesForSession(programId).map(module => {
    if (!schoolAdminHasPrimaryChildren(module)) {
      return {
        key: module.id,
        title: module.label,
        url: module.basePath,
        iconClass: `fa-light ${module.icon}`,
        iconActiveClass: `fa-solid ${module.icon}`,
      }
    }

    return {
      key: module.id,
      title: module.label,
      url: module.basePath,
      iconClass: `fa-light ${module.icon}`,
      iconActiveClass: `fa-solid ${module.icon}`,
      children: module.tabs.map(tab => schoolAdminPrimaryTabChild(module, tab)),
    }
  })
}

export function schoolAdminNavUrls(programId: string | null = null): string[] {
  return collectNavUrls(schoolAdminPrimaryNavSpec(programId))
}

export function sessionCanAccessSchoolAdminPath(
  pathname: string,
  programId: string | null = null,
): boolean {
  return canAccessSchoolAdminPath(pathname, SCHOOL_ADMIN_MODULES, programId)
}

export function schoolAdminContentNote(pathname: string): string {
  const ctx = parseSchoolAdminRoute(pathname)
  if (!ctx) return ""
  if (ctx.secondaryLeaf) return ctx.secondaryLeaf.note
  return ctx.module.notesByHref[pathname] ?? ""
}

export function schoolAdminContentTitle(pathname: string): string {
  const ctx = parseSchoolAdminRoute(pathname)
  if (!ctx) return ""
  if (ctx.secondaryLeaf) return ctx.secondaryLeaf.label
  const tab = ctx.module.tabs.find(row => row.id === ctx.tabId)
  if (tab) return tab.label
  return ctx.module.label
}

export type SchoolAdminBreadcrumb = {
  label: string
  href: string
}

/**
 * SiteHeader trail: ancestors only. `title` is the selected primary child
 * (Setup, Student, …). Do not put that leaf in `breadcrumbs` — the trail
 * appends `title` as the current page.
 */
export function schoolAdminSiteHeader(pathname: string, consoleLabel: string): {
  breadcrumbs: SchoolAdminBreadcrumb[]
  title: string
  documentTitle: string
} {
  const ctx = parseSchoolAdminRoute(pathname)
  if (!ctx) {
    return {
      breadcrumbs: [],
      title: consoleLabel,
      documentTitle: consoleLabel,
    }
  }

  const tab = ctx.module.tabs.find(row => row.id === ctx.tabId)
  const nestedInSidebar = schoolAdminHasPrimaryChildren(ctx.module)
  const pageTitle = nestedInSidebar && tab ? tab.label : ctx.module.label
  const breadcrumbs: SchoolAdminBreadcrumb[] = [
    { label: consoleLabel, href: SCHOOL_ADMIN_DEFAULT_HREF },
  ]
  if (nestedInSidebar && tab) {
    breadcrumbs.push({
      label: ctx.module.label,
      href: schoolAdminModuleHref(ctx.module),
    })
  }

  return {
    breadcrumbs,
    title: pageTitle,
    documentTitle: nestedInSidebar
      ? `${pageTitle} · ${ctx.module.label}`
      : pageTitle,
  }
}
